/* =========================================================================
 * Download — homepage section (anchor target for the hero "Download App" CTA).
 *
 * Two platform cards (iOS / Android). Android is live on Google Play; iOS is
 * still marked "Coming soon" and rendered as a disabled card. Cards are kept
 * large and prominent so the page has a clear final call to action.
 * ========================================================================= */

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.halyard.bripplex";

const PLATFORMS = [
  {
    key: "ios",
    icon: "apple",
    eyebrow: "Coming soon",
    label: "Download on the",
    store: "App Store",
  },
  {
    key: "android",
    icon: "android",
    label: "Get it on",
    store: "Google Play",
    href: PLAY_STORE_URL,
  },
];

export function Download() {
  return (
    <MKBox
      component="section"
      id="download"
      py={{ xs: 8, md: 12 }}
      px={{ xs: 2, md: 4 }}
      sx={({ palette }) => ({
        background: `linear-gradient(180deg, ${palette.white.main} 0%, #EFF6FF 100%)`,
        borderTop: `1px solid ${palette.grey[200]}`,
      })}
    >
      <Container>
        <Grid container justifyContent="center" textAlign="center" mb={{ xs: 5, md: 7 }}>
          <Grid item xs={12} md={8}>
            <MKTypography
              variant="overline"
              color="primary"
              fontWeight="bold"
              sx={{ letterSpacing: "0.1em" }}
            >
              Download the app
            </MKTypography>
            <MKTypography
              variant="h2"
              color="dark"
              mt={1}
              sx={{ fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15 }}
            >
              Start contributing from your phone
            </MKTypography>
            <MKTypography variant="body1" color="text" mt={2} sx={{ maxWidth: 560, mx: "auto" }}>
              Bripplex is live on Google Play — grab it and start contributing today. The iOS app is
              in active development; we&apos;ll open downloads as soon as the build is public.
            </MKTypography>
          </Grid>
        </Grid>

        <Grid container spacing={3} justifyContent="center">
          {PLATFORMS.map((p) => (
            <Grid item xs={12} sm={6} md={5} key={p.key}>
              <MKBox
                component={p.href ? "a" : "div"}
                href={p.href}
                target={p.href ? "_blank" : undefined}
                rel={p.href ? "noopener noreferrer" : undefined}
                sx={({ palette, borders }) => ({
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                  px: { xs: 3, md: 4 },
                  py: { xs: 2.5, md: 3 },
                  borderRadius: borders.borderRadius.lg,
                  background: palette.dark.main,
                  color: palette.white.main,
                  textDecoration: "none",
                  cursor: p.href ? "pointer" : "not-allowed",
                  opacity: p.href ? 1 : 0.92,
                  transition: "transform 0.2s ease, opacity 0.2s ease",
                  "&:hover": { opacity: 1, transform: "translateY(-2px)" },
                })}
              >
                <Icon sx={{ fontSize: { xs: 36, md: 44 } }}>{p.icon}</Icon>
                <MKBox sx={{ flex: 1, textAlign: "left" }}>
                  <MKTypography
                    variant="caption"
                    sx={({ palette }) => ({
                      color: palette.grey[400],
                      fontSize: 11,
                      letterSpacing: "0.04em",
                    })}
                  >
                    {p.label}
                  </MKTypography>
                  <MKTypography
                    variant="h5"
                    sx={({ palette }) => ({
                      color: palette.white.main,
                      fontWeight: 700,
                      lineHeight: 1.15,
                      letterSpacing: "-0.01em",
                    })}
                  >
                    {p.store}
                  </MKTypography>
                </MKBox>
                {p.eyebrow ? (
                  <MKBox
                    sx={({ palette }) => ({
                      px: 1.25,
                      py: 0.5,
                      borderRadius: "999px",
                      background: "rgba(56, 189, 248, 0.18)",
                      color: palette.info.main,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    })}
                  >
                    {p.eyebrow}
                  </MKBox>
                ) : (
                  <Icon sx={{ fontSize: 22, opacity: 0.7 }}>arrow_forward</Icon>
                )}
              </MKBox>
            </Grid>
          ))}
        </Grid>
      </Container>
    </MKBox>
  );
}
